/**
 * Staging orphan-room detection (W-01, P0) — PURE, no React / no store.
 * ============================================================================
 *
 * ## The bug
 *
 * A staging room key IS the uploaded photo's `gcFilename` (§10.3 — feed keys are
 * stable identifiers matching DTO keys, `roomKey === PfV2StagingVisual.id`), and
 * the backend stamps every uploaded visual with the purchase session it was
 * uploaded under:
 *
 *   `a6d04475-b94b-41bf-ac82-72c7b3f07d27-backbonephoto-002.png`
 *    └────────────── sessionId ─────────────┘ └── original name ──┘
 *
 * `draft.sessionId` is deliberately NOT persisted in `OrderDraftDTO`
 * (`draftSlice.hydrateFromDraft` nulls it, `useEnsurePfV2Session` mints a fresh
 * one on resume). The visual endpoints are session-keyed
 * (`/order/{sessionId}/visual/{filename}`), so after a resume the restored rooms
 * point at photos the NEW session cannot resolve: thumbnails render broken, and —
 * the actual P0 — `isComplete` stayed `true`, so the order could be placed with
 * its source photos gone (live repro 2026-08-11, walk-E steps 04–05).
 *
 * ## The rule
 *
 * A room is ORPHANED when its key carries a session id and that id is not the
 * session the order will actually be placed under. Rooms whose key carries no
 * parseable session id are NOT attributable to a foreign session and are KEPT
 * (fail-open — legacy/hand-authored keys must not be nuked by a filename-shape
 * heuristic).
 *
 * Detection is a `gcFilename` shape parse, NOT a `productCode` parse. There is no
 * longer any sanctioned `productCode` parse in the tree: the one that existed
 * (`legacyProductIdFromCode`, which derived a legacy numeric id for the v1 upload
 * mints) was deleted when uploads moved to the productCode-keyed v2 session-file
 * endpoints. `productCode` is opaque — never parse it.
 *
 * Callers: `store/slices/productConfigSlice.dropOrphanedStagingRooms`, driven
 * from the hydration path (`store/rehydrate.ts`), never from a component mount
 * effect (MODULE-CONTRACT §11.1).
 */

import type { StagingDraftDTO, StagingDraftRoomDTO } from './staging.dto'

/**
 * Leading `<uuid>-` purchase-session prefix on an uploaded visual's
 * `gcFilename`. Case-insensitive; the backend emits lowercase v4 UUIDs but the
 * parse must not hinge on that.
 */
const SESSION_PREFIXED_KEY = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})-/i

/**
 * The purchase-session id a staging room's photo was uploaded under, or `null`
 * when the key carries no session prefix (not attributable → never treated as
 * orphaned).
 */
export const stagingSessionIdFromRoomKey = (roomKey: string): string | null => {
  const match = SESSION_PREFIXED_KEY.exec(roomKey)
  return match ? match[1] : null
}

/** Whether this room's photo belongs to a session OTHER than `currentSessionId`. */
export const isOrphanedStagingRoom = (roomKey: string, currentSessionId: string | null | undefined): boolean => {
  const roomSessionId = stagingSessionIdFromRoomKey(roomKey)
  if (roomSessionId === null) return false // unattributable → keep
  return roomSessionId.toLowerCase() !== (currentSessionId ?? '').toLowerCase()
}

export interface StagingOrphanSweep {
  /** The DTO with orphaned rooms removed (same object identity is NOT promised). */
  dto: StagingDraftDTO
  /** Room keys dropped, in DTO order. Empty ⇒ caller must not write. */
  droppedKeys: string[]
}

/**
 * Pure sweep: partitions a staging DTO's rooms into "resolvable under
 * `currentSessionId`" and "orphaned".
 *
 * `currentSessionId` is normally `null` at resume time (the resumed draft has no
 * session yet and a brand-new one is about to be minted) — which correctly makes
 * every session-prefixed room an orphan. The same-session branch is what keeps
 * this correct if backend-side drafts ever persist the session id
 * (PF-V2-OPEN-ITEMS §4): those rooms' photos WOULD still resolve, so they stay.
 */
export const sweepOrphanedStagingRooms = (
  dto: StagingDraftDTO | undefined,
  currentSessionId: string | null | undefined,
  droppedFiles: ReadonlySet<string> = new Set()
): StagingOrphanSweep => {
  const rooms = dto?.rooms
  if (!rooms) return { dto: { ...(dto ?? {}), rooms: {} }, droppedKeys: [] }

  const kept: Record<string, StagingDraftRoomDTO> = {}
  const droppedKeys: string[] = []

  for (const [roomKey, room] of Object.entries(rooms)) {
    if (isOrphanedStagingRoom(roomKey, currentSessionId) || droppedFiles.has(roomKey)) droppedKeys.push(roomKey)
    else kept[roomKey] = room
  }

  return { dto: { ...dto, rooms: kept }, droppedKeys }
}
